import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowRight,
  Bell,
  Bot,
  CalendarClock,
  CircleDollarSign,
  MessageSquareText,
  Palette,
  Pencil,
  Plug,
  ShieldAlert,
  SlidersHorizontal,
  Star,
  UserRoundCheck,
  type LucideIcon,
} from 'lucide-react';
import { AutoPostPreview } from '@/components/organisms/reviews-dashboard/AutoPostPreview';
import { Button } from '@/components/atoms/button';
import { NumberInput } from '@/components/molecules/NumberInput';
import { SettingsSummaryRow } from '@/components/molecules/SettingsSummaryRow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Skeleton } from '@/components/atoms/skeleton';
import { Switch } from '@/components/atoms/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { DetailPageLayout } from '@/components/layouts';
import { SettingsPageHeader } from '@/components/layouts/SettingsPageHeader';
import { ReminderPolicyForm } from '@/components/organisms/settings-reminders/ReminderPolicyForm';
import { ReminderPolicyReadOnlyView } from '@/components/organisms/settings-reminders/ReminderPolicyReadOnlyView';
import { ReviewPlatformsPanel } from '@/components/organisms/settings-review-platforms/ReviewPlatformsPanel';
import { buildChangedSettingsPatch } from '@/components/settings/contracts/contractSettingsDiff';
import { Fullbleed } from '@/contexts/fullbleed';
import {
  useContractSettings,
  useUpdateContractSettings,
} from '@/hooks/contracts/useContractSettings';
import { useReminderPolicy } from '@/hooks/reminder-policies/useReminderPolicy';
import { useHasAnyPermission, useHasPermission } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import {
  REMINDER_TYPE_DESCRIPTIONS,
  REMINDER_TYPE_LABELS,
  REVIEW_POLICY_VIEW_PERMISSIONS,
  REVIEW_REQUEST_POLICY_TYPE,
  type ReminderPolicy,
} from '@/types/reminder-policy';

/**
 * NOT-576 — review settings used to live in three places: the request rules
 * here, the `REVIEW_REQUEST` reminder policy under Settings > Reminders, and the
 * platform connections under Settings > Review Platforms. All three are tabs on
 * this one screen now. Each is a pure move: the same components render against
 * the same rows, over the same endpoints, behind the same permissions — only
 * the address changed.
 */
type ReviewsTab = 'requests' | 'reminder' | 'platforms' | 'auto-post';

const REVIEWS_TABS: ReadonlyArray<{ value: ReviewsTab; label: string; icon: LucideIcon }> = [
  { value: 'requests', label: 'Request rules', icon: SlidersHorizontal },
  { value: 'reminder', label: 'Reminder policy', icon: Bell },
  { value: 'platforms', label: 'Platforms', icon: Plug },
  { value: 'auto-post', label: 'Auto-post preview', icon: Bot },
];

/**
 * The tab strip's classes, exported so the one property that broke can be
 * asserted.
 *
 * `pb-2`, not `pb-0`. The active-tab marker is an `::after` rule on the trigger
 * at `bottom-[-5px]`, and `TabsList` is `overflow-y-hidden` — so a list with no
 * bottom padding renders that marker at full opacity and then clips it. Every
 * tab looked identical and the focus ring on whichever one you last clicked was
 * the only hint. 8px clears the 5px offset plus the 2px rule.
 *
 * A constant rather than an inline string because this is CSS-only and jsdom
 * computes no layout: nothing rendered in a unit test can tell whether the
 * marker is visible. Asserting the class that reserves the room is the closest
 * a test can get, and it is the exact edit that caused the bug.
 */
/**
 * A segmented control, using the atom's `default` variant rather than `line`.
 *
 * The pill styling was first written as overrides on top of `variant="line"`,
 * and the active tab came out invisible: the line variant's own
 * `data-[state=active]:!bg-transparent` is a compound group-data selector, so
 * it outranked the override's `!bg-[#252722]` while `!text-white` still
 * applied — white text on a white strip. `default` already IS this control
 * (muted trough, raised active pill), so it needs no overrides and no
 * hardcoded hex to fight.
 */
export const REVIEWS_TABS_LIST_CLASS =
  'h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl border bg-background p-1.5 pb-2';

const DEFAULT_REVIEWS_TAB: ReviewsTab = 'requests';

function isReviewsTab(value: unknown): value is ReviewsTab {
  return REVIEWS_TABS.some((tab) => tab.value === value);
}

/**
 * `tab`, plus everything the retired /settings/review-platforms address carried.
 * The OAuth callback lands on this screen with `status`, `provider` and
 * `oauthTokenId` set, and `locationId` scopes the platform settings for a
 * multi-location tenant. All of them have to survive `validateSearch` — a param
 * this function drops is a param the connect flow never sees.
 */
interface ReviewsSearch {
  tab?: ReviewsTab;
  /** OAuth-callback signal — `select-business` or `error`. */
  status?: 'select-business' | 'error';
  /** Provider slug echoed back by the operations callback. */
  provider?: 'google' | 'facebook';
  /** ID of the OAuthToken row created by the operations callback. */
  oauthTokenId?: string;
  /** Optional URL-encoded reason if `status=error`. */
  reason?: string;
  /** Multi-location tenants only. */
  locationId?: string;
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/reviews')({
  // The active tab lives in the URL (?tab=...) so it survives a refresh, and so
  // the redirects from the retired /settings/reminders/REVIEW_REQUEST and
  // /settings/review-platforms paths can land directly on the tab that replaced
  // them. `tab` stays optional — Request rules is the default, so a bare
  // /dashboard/settings/reviews carries no param and the existing links to this
  // page keep working untouched.
  validateSearch: (search: Record<string, unknown>): ReviewsSearch => ({
    ...(isReviewsTab(search.tab) && search.tab !== DEFAULT_REVIEWS_TAB ? { tab: search.tab } : {}),
    status:
      search.status === 'select-business' || search.status === 'error' ? search.status : undefined,
    provider:
      search.provider === 'google' || search.provider === 'facebook' ? search.provider : undefined,
    oauthTokenId: search.oauthTokenId ? String(search.oauthTokenId) : undefined,
    reason: search.reason ? String(search.reason) : undefined,
    locationId: search.locationId ? String(search.locationId) : undefined,
  }),
  component: ReviewsSettingsPage,
});

type BillingMode = 'per_visit' | 'subscription' | 'prepaid';
type Strategy =
  | 'every_visit'
  | 'first_visit_only'
  | 'cadence'
  | 'milestone'
  | 'triggered_only'
  | 'disabled';

interface StrategyMap {
  per_visit: Strategy;
  subscription: Strategy;
  prepaid: Strategy;
}

const DEFAULT_STRATEGIES: StrategyMap = {
  per_visit: 'every_visit',
  subscription: 'cadence',
  prepaid: 'milestone',
};

const BILLING_MODES: ReadonlyArray<{
  key: BillingMode;
  label: string;
  description: string;
  icon: typeof CalendarClock;
}> = [
  {
    key: 'per_visit',
    // Named after `BillingMode.PER_VISIT`, but it is the catch-all: a Job's
    // `pricingTermsId` is nullable, and `resolveDefaultReviewStrategy` returns
    // `tenantDefaults.per_visit` for anything without a billing mode
    // (`review-request.service.ts:82`, pinned by the `[undefined, …]` case in
    // `review-request-settings.spec.ts`). So every ordinary one-off job lands
    // here. Describing it as recurring work invited a tenant who runs no
    // per-visit contracts to set it to Disabled and silently switch review
    // requests off for their whole job flow.
    label: 'Per visit',
    description: 'One-off jobs, and recurring work invoiced per visit',
    icon: CalendarClock,
  },
  {
    key: 'subscription',
    label: 'Subscription',
    description: 'Recurring work billed on a subscription',
    icon: CircleDollarSign,
  },
  {
    key: 'prepaid',
    label: 'Prepaid',
    description: 'Recurring work paid before service',
    icon: UserRoundCheck,
  },
];

const STRATEGY_OPTIONS: ReadonlyArray<{
  value: Strategy;
  label: string;
  detail: string;
}> = [
  {
    value: 'every_visit',
    label: 'Every completed visit',
    detail: 'Ask after each eligible completed visit',
  },
  {
    value: 'first_visit_only',
    label: 'First completed visit only',
    detail: 'Ask once, after the first eligible visit',
  },
  {
    value: 'cadence',
    label: 'Every 90 days',
    detail: 'Ask at most once every 90 days',
  },
  {
    value: 'milestone',
    label: 'Visits 1, 4 and 12',
    detail: 'Ask only after these service milestones',
  },
  {
    value: 'triggered_only',
    label: 'Manual trigger only',
    detail: 'Staff decides when to send a request',
  },
  {
    value: 'disabled',
    label: 'Disabled',
    detail: 'Do not create review requests automatically',
  },
];

const RECOVERY_OWNERS = [
  { value: 'account_owner', label: 'Account owner' },
  { value: 'tenant_admin', label: 'Tenant admin' },
  { value: 'escalation_queue', label: 'Escalation queue' },
] as const;

/**
 * Exported deliberately, matching `integrations.tsx`. `autoCodeSplitting` turns
 * `Route.component` into a lazy reference, so a spec rendering it through the
 * route suspends with no boundary — the tab tests drive this component directly
 * instead. The plugin warns the export is not code-split; the cost is bounded to
 * this one route chunk. Do not un-export without giving the specs a Suspense
 * boundary first.
 */
export function ReviewsSettingsPage() {
  const search = Route.useSearch();
  const { tab: tabParam } = search;
  const navigate = Route.useNavigate();
  // Each tab keeps the gate of the screen it came from — the request rules are
  // contract settings, the policy is a reminder policy. These two checks decide
  // which tabs a given operator is offered, so neither cohort gains the other's.
  // The Settings > Reviews nav entry is the union of exactly these two gates
  // (`nav-config.ts`), which is why the policy trio is a shared constant: the
  // entry has to be visible precisely when at least one tab is.
  const canViewRequestRules = useHasPermission('contracts:view');
  const canViewPolicy = useHasAnyPermission(...REVIEW_POLICY_VIEW_PERMISSIONS);
  const canEditPolicy = useHasPermission('reminders:edit');
  // NOT-576 §8.3 — the platforms tab keeps the gate its own page carried:
  // `reviews:view` reads the connected platforms, `reviews:connect` performs the
  // OAuth dance and the connect/disconnect/reorder mutations.
  const canViewPlatforms = useHasPermission('reviews:view');
  const canConnectPlatforms = useHasPermission('reviews:connect');
  const visibleTabs = useMemo(
    () =>
      REVIEWS_TABS.filter((item) => {
        if (item.value === 'requests') return canViewRequestRules;
        if (item.value === 'reminder') return canViewPolicy;
        // Auto-post preview reads what we are about to say in public, which is
        // the same `reviews:view` the platforms list needs.
        return canViewPlatforms;
      }),
    [canViewRequestRules, canViewPolicy, canViewPlatforms],
  );
  // A bare /dashboard/settings/reviews opens on the first tab this operator can
  // read, so a reminders-only user arriving from the nav lands on the policy
  // rather than on request rules they cannot see. An explicit `?tab=` is always
  // honoured — the tab itself renders the denial when it is not theirs.
  // A returning OAuth round trip has to land on the tab that finishes it.
  // `OAuthConnectFlow` builds its `returnTo` from `window.location.pathname`
  // alone, so `?tab=platforms` is exactly what gets dropped on the way out —
  // without this the contractor comes back to Request rules holding a token and
  // the business picker never opens. Keyed on the callback's own params rather
  // than on `returnTo`, so it covers the operations service's default redirect
  // and a bookmark of the old address just as well.
  const returningFromOAuth = search.status !== undefined || search.oauthTokenId !== undefined;
  const tab: ReviewsTab = returningFromOAuth
    ? 'platforms'
    : (tabParam ?? visibleTabs[0]?.value ?? DEFAULT_REVIEWS_TAB);
  const { data, isLoading } = useContractSettings({ enabled: canViewRequestRules });
  const updateSettings = useUpdateContractSettings();
  const settings = data as Record<string, Record<string, unknown>> | undefined;
  const persisted = settings?.reviews;
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [editing, setEditing] = useState(false);

  // The review-request reminder policy, moved here from Settings > Reminders.
  // Same hooks, same permission gates, same endpoints — only the address moved.
  const [policyEditOpen, setPolicyEditOpen] = useState(false);
  const onPolicyTab = tab === 'reminder';
  const onPlatformsTab = tab === 'platforms';
  const onAutoPostTab = tab === 'auto-post';
  // Gated on `canViewPolicy`, not on the tab alone: `?tab=reminder` can be typed
  // or bookmarked by an operator this screen never offers the tab to, and the
  // API would only refuse the read. Mirrors the `enabled` flag the request-rules
  // tab passes to `useContractSettings`, so neither tab fetches from behind its
  // own gate. The gate stays the trio the Reminders screen used, so a
  // `company:view` viewer fetches exactly as it did before the move.
  const {
    data: policy,
    isLoading: policyLoading,
    isError: policyError,
  } = useReminderPolicy(onPolicyTab && canViewPolicy ? REVIEW_REQUEST_POLICY_TYPE : undefined);

  useEffect(() => {
    if (persisted) setDraft(persisted);
  }, [persisted]);

  const patch = useMemo(() => buildChangedSettingsPatch(draft, persisted), [draft, persisted]);
  const dirty = Object.keys(patch).length > 0;
  // Edit mode belongs to the request-rules tab. It is suppressed while that tab
  // is away, so the header can never offer a Save that targets the wrong
  // settings object — but it is not *cleared*: a visit to the policy tab must
  // not turn unsaved edits into a read-only view that reads as saved.
  const contractEditing = editing && tab === 'requests';

  const update = (key: string, value: unknown) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const discard = () => {
    if (persisted) setDraft(persisted);
    setEditing(false);
  };

  const save = () => {
    updateSettings.mutate(
      { tab: 'reviews', patch },
      {
        onSuccess: () => setEditing(false),
      },
    );
  };

  const changeTab = (value: string) => {
    if (!isReviewsTab(value) || value === tab) return;
    // `editing` deliberately survives — see `contractEditing`. Unsaved request
    // rules come back as unsaved request rules, never as persisted ones.
    setPolicyEditOpen(false);
    // `replace` keeps tab-switching out of the history stack, so Back leaves the
    // page rather than walking every tab the operator clicked.
    // `locationId` scopes the platform settings and belongs to the screen
    // rather than to one tab, so it survives the switch. The OAuth params
    // deliberately do not: leaving the tab abandons that round trip, and a
    // stale `oauthTokenId` left in the URL would re-open the picker on return.
    void navigate({
      search: {
        ...(search.locationId ? { locationId: search.locationId } : {}),
        ...(value === DEFAULT_REVIEWS_TAB ? {} : { tab: value }),
      },
      replace: true,
    });
  };

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <SettingsPageHeader
            title="Reviews"
            meta={
              contractEditing ? (
                <span className="hidden text-xs text-muted-foreground sm:inline">Editing</span>
              ) : null
            }
            actions={
              // The platforms and auto-post tabs carry their own controls, so
              // the page-level Edit/Save pair has nothing to act on there.
              // Leaving it visible offers an Edit that does nothing on the
              // screen in front of you and silently opens the request-rules
              // form behind it, which you then meet mid-edit on the next tab
              // switch.
              onPlatformsTab || onAutoPostTab ? null : onPolicyTab ? (
                policyEditOpen ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPolicyEditOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" form="reminder-policy-form">
                      Save
                    </Button>
                  </>
                ) : canEditPolicy ? (
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Edit ${REMINDER_TYPE_LABELS[REVIEW_REQUEST_POLICY_TYPE]} reminder settings`}
                    disabled={policyLoading || !policy}
                    onClick={() => setPolicyEditOpen(true)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                ) : null
              ) : !canViewRequestRules ? null : contractEditing ? (
                <>
                  {dirty ? (
                    <span className="hidden text-xs text-muted-foreground sm:inline">
                      Unsaved changes
                    </span>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={discard}
                    disabled={updateSettings.isPending}
                  >
                    Discard
                  </Button>
                  <Button size="sm" onClick={save} disabled={!dirty || updateSettings.isPending}>
                    {updateSettings.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                </div>
              )
            }
          />
        }
        headerClassName="min-h-[57px] px-4 py-0 sm:px-6"
        contentClassName="bg-[#f4f4f1] p-0"
      >
        <main
          className={cn(
            'mx-auto w-full max-w-[1360px] px-4 py-5 sm:px-6 sm:py-7',
            !isLoading && persisted && 'min-h-full',
          )}
        >
          <Tabs value={tab} onValueChange={changeTab}>
            <TabsList
              className={REVIEWS_TABS_LIST_CLASS}
              aria-label="Review settings sections"
            >
              {visibleTabs.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger
                    key={item.value}
                    value={item.value}
                    className="h-9 shrink-0 rounded-lg px-4 text-[13px] data-[state=active]:bg-[#252722] data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    <Icon className="size-3.5" />
                    {item.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          <div className="mt-4">
            {tab !== 'platforms' ? <ReviewTabIntro tab={tab} /> : null}
            {onAutoPostTab ? (
              !canViewPlatforms ? (
                <AccessDeniedPanel>
                  You need the <span className="font-mono">reviews:view</span> permission to see
                  pending auto-posts.
                </AccessDeniedPanel>
              ) : (
                <AutoPostPreview locationId={search.locationId ?? null} />
              )
            ) : onPlatformsTab ? (
              !canViewPlatforms ? (
                <AccessDeniedPanel>
                  You need the <span className="font-mono">reviews:view</span> permission to view
                  review platforms.
                </AccessDeniedPanel>
              ) : (
                <ReviewPlatformsPanel
                  locationId={search.locationId ?? null}
                  canEdit={canConnectPlatforms}
                  oauthCallback={{
                    status: search.status,
                    provider: search.provider,
                    oauthTokenId: search.oauthTokenId,
                    reason: search.reason,
                  }}
                  onClearOAuthParams={() => {
                    void navigate({
                      search: {
                        ...(search.locationId ? { locationId: search.locationId } : {}),
                        tab: 'platforms',
                      },
                      replace: true,
                    });
                  }}
                />
              )
            ) : onPolicyTab ? (
              <ReviewRequestPolicyTab
                canView={canViewPolicy}
                isLoading={policyLoading}
                isError={policyError}
                policy={policy}
                editing={policyEditOpen}
                onSaved={() => setPolicyEditOpen(false)}
              />
            ) : !canViewRequestRules ? (
              <AccessDeniedPanel>
                You need the <span className="font-mono">contracts:view</span> permission to view
                review request rules.
              </AccessDeniedPanel>
            ) : isLoading || !persisted ? (
              <ReviewsSkeleton />
            ) : contractEditing ? (
              <ReviewsEdit draft={draft} update={update} />
            ) : (
              <ReviewsDetail settings={draft} />
            )}
          </div>
        </main>
      </DetailPageLayout>
    </>
  );
}

function ReviewTabIntro({ tab }: { tab: Exclude<ReviewsTab, 'platforms'> }) {
  const content = {
    requests: {
      eyebrow: 'Request strategy',
      title: 'Ask at the right moment.',
      detail: 'Set the default review-request timing for every kind of service visit.',
    },
    reminder: {
      eyebrow: 'Delivery policy',
      title: 'Make every follow-up feel considered.',
      detail: 'Control the cadence, channels, quiet hours, and message templates behind each review request.',
    },
    'auto-post': {
      eyebrow: 'Public replies',
      title: 'Review what speaks for your business.',
      detail: 'See every AI reply that is scheduled to post and stop anything that needs a human review.',
    },
  }[tab];

  return (
    <section className="mb-5 rounded-2xl border border-stone-200 bg-[#fcfbf8] px-5 py-5 shadow-[0_12px_32px_-28px_rgba(57,47,38,0.42)] sm:px-6">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#5d685d]">
        {content.eyebrow}
      </p>
      <h2 className="mt-2 text-2xl font-semibold tracking-[-0.035em] text-[#25231f]">
        {content.title}
      </h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">{content.detail}</p>
    </section>
  );
}

function AccessDeniedPanel({ children }: { children: ReactNode }) {
  return (
    <div className="max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-4">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="size-4" />
        <h2 className="text-sm font-medium">Access denied</h2>
      </div>
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

/**
 * The `REVIEW_REQUEST` reminder policy, rendered exactly as Settings >
 * Reminders rendered it: the same read-only view over the same fetched row,
 * behind the same `reminders:*` / `company:view` gates.
 */
function ReviewRequestPolicyTab({
  canView,
  isLoading,
  isError,
  policy,
  editing,
  onSaved,
}: {
  canView: boolean;
  isLoading: boolean;
  isError: boolean;
  policy: ReminderPolicy | undefined;
  editing: boolean;
  onSaved: () => void;
}) {
  if (!canView) {
    return (
      <AccessDeniedPanel>
        You need the <span className="font-mono">reminders:view</span> permission to view reminder
        policies.
      </AccessDeniedPanel>
    );
  }

  const cadence = policy && Array.isArray(policy.cadence) ? policy.cadence : [];
  const channels = [...new Set(cadence.map((step) => step.channel))];

  return (
    <div className="space-y-5">
      {/* The same strip that opens the other two tabs, over the four facts that
          decide whether this policy does anything at all. */}
      {policy && !editing ? (
        <SettingsSummaryRow
          stats={[
            {
              label: 'Status',
              value: policy.enabled ? 'On' : 'Off',
              // A disabled review-request policy means no request ever goes
              // out, whatever the request rules on the first tab say.
              tone: policy.enabled ? 'default' : 'attention',
            },
            { label: 'Channels', value: channels.length > 0 ? channels.join(', ') : 'None' },
            { label: 'Cadence steps', value: String(cadence.length) },
            { label: 'Quiet hours', value: policy.quietHours ? 'On' : 'Off' },
          ]}
        />
      ) : null}

      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="text-sm font-semibold">
            {REMINDER_TYPE_LABELS[REVIEW_REQUEST_POLICY_TYPE]}
          </h2>
          <p className="text-[13px] text-muted-foreground">
            {REMINDER_TYPE_DESCRIPTIONS[REVIEW_REQUEST_POLICY_TYPE]}
          </p>
        </div>

        {isLoading ? (
          <div className="grid gap-4 xl:grid-cols-2">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-52 w-full rounded-xl xl:col-span-2" />
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load reminder policy. Please retry.
          </div>
        ) : policy && editing ? (
          <ReminderPolicyForm
            policy={policy}
            formId="reminder-policy-form"
            onSaved={onSaved}
          />
        ) : policy ? (
          <ReminderPolicyReadOnlyView policy={policy} />
        ) : (
          <div className="rounded-xl border bg-card p-4 text-sm text-muted-foreground">
            No reminder policy found for this type.
          </div>
        )}
      </section>
    </div>
  );
}

export function ReviewsDetail({ settings }: { settings: Record<string, unknown> }) {
  const strategies = strategyMapFrom(settings);
  const threshold = numberFrom(settings.publicReviewThreshold, 4);
  // Recovery is its own band now, so it is read rather than derived. The
  // fallback re-derives from this tenant's own public threshold — the same rule
  // the backend resolver applies — because falling back to a flat 3 would tell
  // a tenant on a 5-star threshold that its four-star customers get no recovery
  // when in fact they do.
  const recoveryThreshold = numberFrom(
    settings.serviceRecoveryThreshold,
    Math.max(0, threshold - 1),
  );
  const recoveryOwner = ownerLabel(String(settings.negativeRatingRecoveryOwner ?? 'account_owner'));
  // The one thing worth knowing about these six strategies at a glance: does
  // anything go out without someone pressing send? `triggered_only` and
  // `disabled` are the two modes where nothing does.
  const automaticModes = BILLING_MODES.filter(
    (mode) => strategies[mode.key] !== 'triggered_only' && strategies[mode.key] !== 'disabled',
  ).length;

  return (
    <div className="space-y-5">
      <SettingsSummaryRow
        stats={[
          {
            label: 'Automatic request rules',
            value: `${automaticModes} of ${BILLING_MODES.length} modes`,
            // Nothing going out at all is a state a tenant can legitimately
            // choose, and also the one a misconfigured tenant sits in without
            // noticing. Flagging it costs the deliberate case one amber word.
            tone: automaticModes === 0 ? 'attention' : 'default',
          },
          { label: 'Ask again if last rated', value: `${threshold}+` },
          {
            label: 'Follow up on ratings',
            value: recoveryThreshold === 0 ? 'Never' : `${recoveryThreshold}★ or less`,
          },
        ]}
      />

      <SettingsSection
        icon={CalendarClock}
        title="When a review request is sent"
        summary="The schedule applied after eligible work is completed, based on how it is billed"
      >
        <div className="divide-y divide-border/60">
          {BILLING_MODES.map((mode) => {
            const strategy = strategyDescriptor(strategies[mode.key]);
            const Icon = mode.icon;
            return (
              <div
                key={mode.key}
                className="grid gap-3 px-5 py-4 lg:grid-cols-[13rem_minmax(0,1fr)] lg:items-center"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted">
                    <Icon className="size-4 text-muted-foreground" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{mode.label}</p>
                    <p className="text-[13px] leading-5 text-muted-foreground lg:truncate">
                      {mode.description}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-[13px] font-medium leading-5">{strategy.label}</p>
                  <p className="text-[13px] leading-5 text-muted-foreground">{strategy.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </SettingsSection>

      <SettingsSection
        icon={ShieldAlert}
        title="When we hold back"
        summary="Customer fatigue and account-health gates"
        footer={<DisputeHoldNote />}
      >
        <div className="grid divide-y divide-border/60 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
          <DetailMetric
            label="Request cooldown"
            value={`${numberFrom(settings.cooldownDays, 60)} days`}
            detail="Minimum time between requests to the same customer"
          />
          <DetailMetric
            label="Unpaid invoice"
            value={`${unpaidInvoiceAgeFrom(settings)} days`}
            detail="Hold when an unpaid invoice is older than this"
          />
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Star}
        title="What happens after a customer responds"
        summary="Every customer sees the same connected review platforms; lower ratings can also open a follow-up task"
        footer={
          <div className="grid border-t border-border/60 bg-muted/30 sm:grid-cols-3 sm:divide-x sm:divide-border/60">
            <FooterFlag
              label="Balance across platforms"
              enabled={Boolean(settings.adaptivePlatformRouting ?? true)}
            />
            <FooterFlag
              label="Technician reputation"
              enabled={Boolean(settings.techReputationTracking ?? true)}
            />
            {/*
              Platforms is a tab on this very screen since NOT-576 H. Pointing
              at /settings/review-platforms still worked — that address is a
              redirect now — but it left the page and bounced back into it, and
              the redirect discards nothing only by accident. Link to the tab.
            */}
            <Link
              to="/dashboard/settings/reviews"
              search={{ tab: 'platforms' }}
              className="flex min-h-14 items-center justify-between gap-3 px-5 py-3 text-sm font-medium transition-colors hover:bg-muted/60"
            >
              Review platform credentials
              <ArrowRight className="size-3.5 text-muted-foreground" />
            </Link>
          </div>
        }
      >
        <div className="grid gap-2 px-5 py-5 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-stretch lg:gap-0">
          <FlowStep
            icon={<MessageSquareText className="size-4" />}
            title="Review request"
            detail="An eligible customer receives an email or text invitation"
          />
          <FlowArrow />
          <FlowStep
            icon={<Star className="size-4" />}
            title="Rating and review"
            detail="They can rate their experience, choose a public platform, or leave a private note"
          />
          <FlowArrow />
          <div className="grid gap-2 lg:grid-rows-2">
            {/*
              Both readings say "every connected platform", because every
              customer is offered all of them whatever they rated. The switch
              only changes the ORDER, and it turns on how many reviews the
              business already holds — never on the rating.
            */}
            <FlowOutcome
              tone="positive"
              // Titled by the rating band, like its recovery sibling below, and
              // it must name the WHOLE range. A neutral title such as "Public
              // review options" reads, next to a sibling titled "1-3 stars", as
              // though public platforms were the complement of the recovery
              // band — which is rating-gating, the exact claim this screen must
              // not make. Guarded by reviews.detail.test.tsx.
              title="Every rating, 1-5 stars"
              detail={
                (settings.adaptivePlatformRouting ?? true)
                  ? 'Sees every connected platform, the thinnest listing first'
                  : 'Sees every connected platform, in your configured order'
              }
            />
            <FlowOutcome
              tone="recovery"
              title={recoveryThreshold === 0 ? 'No recovery band' : `1-${recoveryThreshold} stars`}
              detail={
                recoveryThreshold === 0
                  ? 'No rating opens a recovery task'
                  : `Create a recovery task for ${recoveryOwner.toLowerCase()}`
              }
            />
          </div>
        </div>
      </SettingsSection>

      <PublicStoriesPreview settings={settings} />
    </div>
  );
}

function PublicStoriesPreview({ settings }: { settings: Record<string, unknown> }) {
  const headline = String(settings.publicStoriesHeadline ?? PUBLIC_STORY_DEFAULTS.publicStoriesHeadline);
  const description = String(
    settings.publicStoriesDescription ?? PUBLIC_STORY_DEFAULTS.publicStoriesDescription,
  );
  const accent = String(settings.publicStoriesAccentColor ?? PUBLIC_STORY_DEFAULTS.publicStoriesAccentColor);
  const showMap = Boolean(settings.publicStoriesShowMap ?? PUBLIC_STORY_DEFAULTS.publicStoriesShowMap);

  return (
    <SettingsSection
      icon={Palette}
      title="Public story page"
      summary="The page customers see when you share a completed job"
    >
      <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-center">
        <div>
          <p className="text-sm font-semibold">{headline}</p>
          <p className="mt-1 max-w-xl text-[13px] leading-5 text-muted-foreground">{description}</p>
          <p className="mt-3 text-[13px] text-muted-foreground">
            {showMap ? 'Map and completed-work details are visible.' : 'Completed-work details are visible.'}
          </p>
        </div>
        <div className="overflow-hidden rounded-lg border bg-background shadow-sm">
          {showMap ? <div className="h-14 bg-muted" /> : null}
          <div className="p-3.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: accent }}>
              Completed work
            </p>
            <p className="mt-1 text-sm font-semibold">{headline}</p>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}

function ReviewsEdit({
  draft,
  update,
}: {
  draft: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}) {
  const [tab, setTab] = useState<'requests' | 'public'>('requests');
  const strategies = strategyMapFrom(draft);
  const setStrategy = (mode: BillingMode, value: Strategy) => {
    update('defaultStrategyByBillingMode', { ...strategies, [mode]: value });
  };

  return (
    <div className="space-y-5">
      {/* Same card edge and the same segmented control the rating pickers below
          use, so entering edit mode changes what you can do rather than what
          the screen looks like. */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-5 py-3.5 shadow-sm">
        <div>
          <p className="text-sm font-semibold">Review request rules</p>
          <p className="text-[13px] leading-5 text-muted-foreground">
            Choose when customers receive an invitation and what happens after they respond.
          </p>
        </div>
        <div className="inline-flex rounded-md border bg-muted p-0.5">
          <button
            type="button"
            onClick={() => setTab('requests')}
            className={cn(
              'h-8 rounded px-3 text-[13px] font-medium transition-colors',
              tab === 'requests'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Review requests
          </button>
          <button
            type="button"
            onClick={() => setTab('public')}
            className={cn(
              'h-8 rounded px-3 text-[13px] font-medium transition-colors',
              tab === 'public'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Public story page
          </button>
        </div>
      </div>
      {tab === 'public' ? (
        <PublicStoriesSettings draft={draft} update={update} />
      ) : (
        <div className="space-y-5">
          <SettingsSection
            icon={CalendarClock}
            title="When to send a review request"
            summary="Choose the timing that makes sense for each way you bill customers"
          >
            <div className="divide-y divide-border/60">
              {BILLING_MODES.map((mode) => {
                const Icon = mode.icon;
                return (
                  <div
                    key={mode.key}
                    className="grid gap-3 px-5 py-4 lg:grid-cols-[13rem_minmax(18rem,32rem)_1fr] lg:items-center"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted">
                        <Icon className="size-4 text-muted-foreground" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold">{mode.label}</p>
                        <p className="text-[13px] leading-5 text-muted-foreground">
                          {mode.description}
                        </p>
                      </div>
                    </div>
                    <Select
                      value={strategies[mode.key]}
                      onValueChange={(value) => setStrategy(mode.key, value as Strategy)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STRATEGY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[13px] leading-5 text-muted-foreground">
                      {strategyDescriptor(strategies[mode.key]).detail}
                    </p>
                  </div>
                );
              })}
            </div>
          </SettingsSection>

          <SettingsSection
            icon={ShieldAlert}
            title="When we hold back"
            summary="Protect customers from poorly timed or repeated requests"
            footer={<DisputeHoldNote />}
          >
            <div className="grid divide-y divide-border/60 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
              <EditField label="Request cooldown">
                <SuffixedNumberField
                  value={numberFrom(draft.cooldownDays, 60)}
                  min={0}
                  max={365}
                  suffix="days"
                  onChange={(value) => update('cooldownDays', value)}
                />
              </EditField>
              <EditField label="Unpaid invoice age">
                <SuffixedNumberField
                  value={unpaidInvoiceAgeFrom(draft)}
                  min={0}
                  max={365}
                  suffix="days"
                  onChange={(value) => update('skipIfUnpaidInvoiceDays', value)}
                />
              </EditField>
            </div>
          </SettingsSection>

          <SettingsSection
            icon={Star}
            title="After the customer responds"
            summary="Choose who we ask again, and when a poor rating opens a recovery case"
          >
            <div className="grid divide-y divide-border/60 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
              <div className="divide-y divide-border/60">
                {/*
                  Renamed from "Public review threshold". The number no longer
                  has anything to do with public review routing — that gate is
                  deleted, and every rating now reaches the same platforms. All
                  it still does is feed `skipIfLastInternalRatingBelow`, which
                  D10 retains: whether we extend another invitation. A control
                  named after the gate we just removed is an invitation to
                  restore the gate so the label becomes true again.
                */}
                <EditField label="Ask again only if they last rated">
                  <div className="inline-flex rounded-md border bg-muted p-0.5">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        className={cn(
                          'h-8 min-w-10 rounded px-2 text-xs font-medium transition-colors',
                          numberFrom(draft.publicReviewThreshold, 4) === rating
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                        onClick={() => update('publicReviewThreshold', rating)}
                      >
                        {rating}+
                      </button>
                    ))}
                  </div>
                </EditField>
                <EditField label="Open a recovery case at or below">
                  <div className="inline-flex rounded-md border bg-muted p-0.5">
                    {[0, 1, 2, 3, 4].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        className={cn(
                          'h-8 min-w-10 rounded px-2 text-xs font-medium transition-colors',
                          numberFrom(
                            draft.serviceRecoveryThreshold,
                            // Same fallback rule as the read-only view and the
                            // backend resolver — a flat 3 would highlight the
                            // wrong button for a tenant on a 5-star threshold
                            // that has never opened this control.
                            Math.max(0, numberFrom(draft.publicReviewThreshold, 4) - 1),
                          ) === rating
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                        onClick={() => update('serviceRecoveryThreshold', rating)}
                      >
                        {rating === 0 ? 'Never' : `${rating}★ or less`}
                      </button>
                    ))}
                  </div>
                </EditField>
                <EditField label="Negative-rating recovery owner">
                  <Select
                    value={String(draft.negativeRatingRecoveryOwner ?? 'account_owner')}
                    onValueChange={(value) => update('negativeRatingRecoveryOwner', value)}
                  >
                    <SelectTrigger className="w-full sm:w-56">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECOVERY_OWNERS.map((owner) => (
                        <SelectItem key={owner.value} value={owner.value}>
                          {owner.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </EditField>
              </div>
              <div className="divide-y divide-border/60">
                <ToggleField
                  label="Balance reviews across platforms"
                  detail="Offer the platform you have fewest recent reviews on first. Every platform is still shown, to every customer."
                  checked={Boolean(draft.adaptivePlatformRouting ?? true)}
                  onCheckedChange={(value) => update('adaptivePlatformRouting', value)}
                />
                <ToggleField
                  label="Technician reputation"
                  detail="Include private ratings in technician reputation metrics"
                  checked={Boolean(draft.techReputationTracking ?? true)}
                  onCheckedChange={(value) => update('techReputationTracking', value)}
                />
              </div>
            </div>
          </SettingsSection>
        </div>
      )}
    </div>
  );
}

const PUBLIC_STORY_DEFAULTS = {
  publicStoriesHeadline: 'Job stories',
  publicStoriesDescription: 'Completed work, customer reviews, and the story behind each visit.',
  publicStoriesAccentColor: '#111827',
  publicStoriesFont: 'system',
  publicStoriesShowMap: true,
  publicStoriesShowFilters: true,
  publicStoriesShowAreas: true,
  publicStoriesShowPhotos: true,
  publicStoriesShowNotes: true,
  publicStoriesShowTechnician: true,
  publicStoriesShowJobDescription: true,
  publicStoriesShowPlatform: true,
};

function PublicStoriesSettings({
  draft,
  update,
}: {
  draft: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}) {
  const get = <T,>(key: keyof typeof PUBLIC_STORY_DEFAULTS): T =>
    (draft[key] ?? PUBLIC_STORY_DEFAULTS[key]) as T;
  const accent = get<string>('publicStoriesAccentColor');
  return (
    <SettingsSection
      icon={Palette}
      title="Public story page"
      summary="Choose what customers see when you share completed work"
    >
      <div className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-6">
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold">Brand</h3>
              <p className="text-xs text-muted-foreground">
                Make the page feel like your company, not a generic review widget.
              </p>
            </div>
            <label className="grid gap-1.5 text-sm font-medium">
              Headline
              <input
                value={get<string>('publicStoriesHeadline')}
                maxLength={80}
                onChange={(e) => update('publicStoriesHeadline', e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-medium">
              Description
              <textarea
                value={get<string>('publicStoriesDescription')}
                maxLength={160}
                onChange={(e) => update('publicStoriesDescription', e.target.value)}
                className="min-h-20 resize-none rounded-md border bg-background px-3 py-2 font-normal"
              />
            </label>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="grid gap-1.5 text-sm font-medium">
                Font
                <select
                  value={get<string>('publicStoriesFont')}
                  onChange={(e) => update('publicStoriesFont', e.target.value)}
                  className="h-10 rounded-md border bg-background px-3 font-normal"
                >
                  <option value="system">Modern sans</option>
                  <option value="modern">Modern geometric</option>
                  <option value="humanist">Humanist sans</option>
                  <option value="rounded">Friendly rounded</option>
                  <option value="serif">Editorial serif</option>
                  <option value="mono">Technical mono</option>
                </select>
              </label>
              <label className="grid gap-1.5 text-sm font-medium">
                Accent color
                <div className="flex h-10 items-center gap-2 rounded-md border px-2">
                  <input
                    type="color"
                    value={accent}
                    onChange={(e) => update('publicStoriesAccentColor', e.target.value)}
                    className="h-7 w-9 cursor-pointer rounded border-0 bg-transparent p-0"
                  />
                  <span className="text-xs font-normal text-muted-foreground">{accent}</span>
                  <Palette className="ml-auto size-4 text-muted-foreground" />
                </div>
              </label>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold">What to show</h3>
            <p className="mb-2 text-xs text-muted-foreground">
              Keep the public story focused on the work you want to showcase.
            </p>
            <div className="grid gap-1 rounded-lg border sm:grid-cols-2">
              {(
                [
                  ['publicStoriesShowMap', 'Map and locations', 'Visible on shared pages'],
                  ['publicStoriesShowFilters', 'Service filters', 'Visible on shared pages'],
                  ['publicStoriesShowAreas', 'Areas index', 'Visible on shared pages'],
                  ['publicStoriesShowPhotos', 'Job photos', 'Visible on shared pages'],
                  // This one is not a plain on/off: the page only ever publishes
                  // notes somebody marked customer-visible, so say that here
                  // rather than let the toggle imply every job note goes public.
                  [
                    'publicStoriesShowNotes',
                    'Customer-visible notes',
                    'Only notes marked customer-visible are published. Internal and admin-only notes never are.',
                  ],
                  ['publicStoriesShowTechnician', 'Technician name', 'Visible on shared pages'],
                  ['publicStoriesShowJobDescription', 'Job description', 'Visible on shared pages'],
                  ['publicStoriesShowPlatform', 'Review platform', 'Visible on shared pages'],
                ] as const
              ).map(([key, label, detail]) => (
                <ToggleField
                  key={key}
                  label={label}
                  detail={detail}
                  checked={get<boolean>(key)}
                  onCheckedChange={(value) => update(key, value)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-muted/40 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Live preview
          </p>
          <div className="mt-3 overflow-hidden rounded-lg border bg-background shadow-sm">
            <div
              className="h-28 bg-muted"
              style={{ display: get<boolean>('publicStoriesShowMap') ? 'block' : 'none' }}
            >
              <div
                className="m-4 inline-flex rounded-full bg-background px-2 py-1 text-[10px] font-semibold shadow-sm"
                style={{ color: accent }}
              >
                ● Completed work
              </div>
            </div>
            <div className="space-y-2 p-4">
              <p
                className="text-[10px] font-semibold uppercase tracking-[0.16em]"
                style={{ color: accent }}
              >
                Completed work
              </p>
              <p className="text-lg font-semibold tracking-tight">
                {get<string>('publicStoriesHeadline')}
              </p>
              <p className="line-clamp-2 text-xs text-muted-foreground">
                {get<string>('publicStoriesDescription')}
              </p>
              <div className="rounded-md bg-muted/50 p-3">
                <div className="h-2 w-24 rounded bg-muted-foreground/25" />
                <div className="mt-2 h-2 w-full rounded bg-muted-foreground/15" />
                <div className="mt-1 h-2 w-4/5 rounded bg-muted-foreground/15" />
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Changes apply to new share links. Existing links keep their saved presentation.
          </p>
        </div>
      </div>
    </SettingsSection>
  );
}

/**
 * One band of a settings tab, in the shape the Platforms tab established: a
 * 32px icon plate, a 14px title, a 13px summary, then the body — and an
 * optional footer band on the same `bg-muted/30` the Connected Apps cards use
 * for their action row.
 *
 * The old heading put the title left and the summary right, so the two read as
 * unrelated fragments on a wide viewport and the summary lost its subject. It
 * also spent a hardcoded `#55514d`, which had no dark-mode value at all.
 */
function SettingsSection({
  icon: Icon,
  title,
  summary,
  children,
  footer,
}: {
  icon: LucideIcon;
  title: string;
  summary: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
      <div className="flex items-start gap-3 border-b border-border/60 px-5 py-4">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Icon className="size-4 text-muted-foreground" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold leading-5">{title}</h2>
          <p className="mt-0.5 text-[13px] leading-5 text-muted-foreground">{summary}</p>
        </div>
      </div>
      {children}
      {footer}
    </section>
  );
}

/**
 * The open-dispute hold, as a footnote rather than a row.
 *
 * It is not a control: `skipIfCustomerHasOpenDispute` is immutable — the
 * settings schema rejects any PATCH that sets it to anything but `true` (spec
 * §12.11) — so a row reading "Always hold" sat in a band of dials looking like
 * something you could change, and edit mode stated it twice, once as a field
 * and once down here.
 *
 * "Automatic" is doing real work in this sentence. The gate is cleared by
 * `overrideOk` on the manual-trigger path, so staff who know a dispute is a
 * bank error can still send one by hand; it is the automatic ask, on visit
 * completion, that can never be configured to ignore a dispute.
 */
function DisputeHoldNote() {
  return (
    <div className="flex items-center gap-2 border-t border-border/60 bg-muted/30 px-5 py-3 text-[13px] text-muted-foreground">
      <ShieldAlert className="size-4 shrink-0" aria-hidden />
      An open dispute always pauses automatic review requests, and is not configurable.
    </div>
  );
}

function DetailMetric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="min-h-24 px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-1 text-[13px] leading-5 text-muted-foreground">{detail}</p>
        </div>
        <p className="shrink-0 text-sm font-semibold">{value}</p>
      </div>
    </div>
  );
}

function FlowStep({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) {
  return (
    <div className="flex min-h-24 flex-col justify-center rounded-lg border border-border/60 bg-muted/30 p-4">
      <span className="text-muted-foreground">{icon}</span>
      <p className="mt-3 text-sm font-semibold">{title}</p>
      <p className="mt-1 text-[13px] leading-5 text-muted-foreground">{detail}</p>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="hidden w-12 items-center justify-center lg:flex">
      <ArrowRight className="size-4 text-muted-foreground" />
    </div>
  );
}

function FlowOutcome({
  tone,
  title,
  detail,
}: {
  tone: 'positive' | 'recovery';
  title: string;
  detail: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2.5',
        // Emerald and amber, the same two the shared status vocabulary uses for
        // "healthy" and "someone must act" — the recovery band is a task
        // somebody picks up, not a failure, so it is amber and not destructive.
        tone === 'positive'
          ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/60 dark:bg-emerald-950/30'
          : 'border-amber-200 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/30',
      )}
    >
      <p className="text-xs font-semibold">{title}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

/**
 * On/off as a dot plus near-neutral text, which is the vocabulary
 * `integration-tokens.ts` settled on for the Connected Apps list: a row of
 * filled pills reads as decoration, a row of dots reads as a column of state.
 */
function FooterFlag({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="flex min-h-14 items-center justify-between gap-3 px-5 py-3">
      <span className="text-sm font-medium">{label}</span>
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 text-xs font-medium',
          enabled ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <span
          className={cn(
            'size-1.5 rounded-full',
            enabled ? 'bg-emerald-500' : 'bg-muted-foreground/40',
          )}
          aria-hidden
        />
        {enabled ? 'On' : 'Off'}
      </span>
    </div>
  );
}

function EditField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-24 items-center justify-between gap-4 px-5 py-4">
      <p className="text-sm font-medium">{label}</p>
      {children}
    </div>
  );
}

function ToggleField({
  label,
  detail,
  checked,
  onCheckedChange,
}: {
  label: string;
  detail: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex min-h-24 items-center justify-between gap-4 px-5 py-4">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-1 text-[13px] leading-5 text-muted-foreground">{detail}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function SuffixedNumberField({
  value,
  min,
  max,
  suffix,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  suffix: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="relative block w-36">
      <NumberInput
        min={min}
        max={max}
        value={value}
        className="pr-14"
        // An emptied box parks the setting at its floor; the box itself stays
        // empty so the next figure can be typed straight over the old one.
        emptyValue={min}
        onChange={(next) => onChange(next === null ? min : Math.min(max, Math.max(min, next)))}
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
        {suffix}
      </span>
    </label>
  );
}

function ReviewsSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-[72px] w-full rounded-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
      <Skeleton className="h-44 w-full rounded-xl" />
    </div>
  );
}

function strategyMapFrom(settings: Record<string, unknown>): StrategyMap {
  const raw = (settings.defaultStrategyByBillingMode ?? settings.defaultStrategy ?? {}) as Partial<
    Record<BillingMode, string>
  >;
  return {
    per_visit: supportedStrategy(raw.per_visit, DEFAULT_STRATEGIES.per_visit),
    subscription: supportedStrategy(raw.subscription, DEFAULT_STRATEGIES.subscription),
    prepaid: supportedStrategy(raw.prepaid, DEFAULT_STRATEGIES.prepaid),
  };
}

function unpaidInvoiceAgeFrom(settings: Record<string, unknown>) {
  return numberFrom(
    settings.skipIfUnpaidInvoiceDays ?? settings.skipIfUnpaidInvoiceOlderThanDays,
    14,
  );
}

function supportedStrategy(value: string | undefined, fallback: Strategy): Strategy {
  return STRATEGY_OPTIONS.some((option) => option.value === value) ? (value as Strategy) : fallback;
}

function strategyDescriptor(value: Strategy) {
  return STRATEGY_OPTIONS.find((option) => option.value === value) ?? STRATEGY_OPTIONS[0];
}

function numberFrom(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function ownerLabel(value: string): string {
  return RECOVERY_OWNERS.find((owner) => owner.value === value)?.label ?? 'Account owner';
}
